import {ACCEPTED, CREATED} from 'http-status-codes';
import _ from 'lodash';
import {ParsedQs} from 'qs';
import React, {useEffect, useState, FunctionComponent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {
    ProductData,
    ProductInCatalog,
    ProductField,
    UOM,
} from '@treez/commons/sharedTypings/product';
import {requiredAndInvalidProductFieldsByType} from '@treez/commons/utils/product';
import {Button, ButtonClass} from '@treez-ui/core/buttons/Button';
import {Title, TitleSize} from '@treez-ui/core/layout/Title';
import {Section} from '@treez-ui/core/layout';
import {ModalType} from '@treez-ui/core/feedback/modals';
import {StoreAttributes} from '~/lib/directory';
import {searchForOrganization} from '~/lib/entity';
import {ProductValidationErrorReport} from '~/lib/errors';
import {EACH_PRODUCT_TYPES, mapStoreNameToCatalogPresence} from '~/lib/product';
import {parseQueryString} from '~/lib/routing';
import UIWrapper from '~/modules/pages/UIWrapper';
import {
    confirmMerge,
    informUserErrorRetrievingBrandProduct,
    informUserErrorRetrievingProductsToMerge,
    informUserErrorRetrievingStoreInfo,
} from '~/redux/actionCreators/UserFeedbackCreators';
import {highlightMultipleProducts} from '~/redux/actions/HighlightedProductActions';
import {
    clearMergeState,
    setMergeProductStorePresence,
    setMergeToProduct,
} from '~/redux/actions/MergeActions';
import {recieveOrganizationInformation} from '~/redux/actions/OrganizationActions';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {GlobalApplicationState} from '~/redux/reducers/RootReducer';

import {
    selectMergeProduct,
    selectMergeProductsStorePresence,
    selectMergeProductType,
    selectMergingToOrgProduct,
} from '~/redux/selectors/mergeSelectors';
import {
    selectStoresInOrganization,
    selectOrganizationCatalogId,
} from '~/redux/selectors/organizationSelectors';
import {BaseParameters, generateProductMergeRoute} from '~/Routes';
import {
    CatalogProductInformation,
    patchProduct,
    getBrandProductsById,
    getCatalogProductData,
    getProductsById,
    mergeProductsIntoExistingProduct,
    mergeProductsIntoNewProduct,
    ProductsPresenceAcrossStores,
    ProductStorePresenceAttributes,
} from '~/utils/ProductAPI';
import CatalogAssignment from './CatalogAssignment';
import MergeProductDetails from './MergeProductDetails';
import ProductMergeNav from './ProductMergeNav';

const {NAME, TYPE} = ProductField;

export enum MergeParameter {
    BRAND_PRODUCT = 'brandProduct',
    HIDE_EXACT_MATCH = 'hideExactMatch',
    PRODUCTS = 'products',
    PRODUCT_TO_MERGE_TO = 'productToMergeTo',
}

export enum MergeStep {
    MERGE_PRODUCT_DETAILS = 'Merge Product Details',
    SELECT_PRICE = 'Select Price',
}

const FINAL_STEP = 2;

export const STEPS = Object.values(MergeStep);

type Props = RouteComponentProps;

export interface MergeProductParsedQs extends ParsedQs {
    products: string;
    hideExactMatch: string;
    brandProduct?: string;
}

export const ProductMerge: FunctionComponent<Props> = (props) => {
    // Nav and URL
    const {
        history,
        location,
        match: {params},
    } = props;

    const {organizationId} = params as BaseParameters;

    const queryParams = parseQueryString(location.search) as MergeProductParsedQs;

    const productsParam = _.get(queryParams, 'products');

    const productIds = queryParams.products
        ? _.map(_.split(queryParams.products, ','), Number)
        : [];

    const brandProductId = _.toNumber(queryParams.brandProduct);

    const hideExactMatchParam = _.get(queryParams, 'hideExactMatch', false);

    const productToMergeToParam = _.get(queryParams, 'productToMergeTo', undefined) as
        | number
        | undefined;

    const hideExactMatch = hideExactMatchParam === 'true';

    // Global State
    const productToMergeTo = useSelector(selectMergeProduct);
    const mergingToExistingProduct = useSelector(selectMergingToOrgProduct);
    const productToMergeToType = useSelector(selectMergeProductType);

    const stores = useSelector<GlobalApplicationState, StoreAttributes[] | undefined>(
        selectStoresInOrganization
    );

    const orgCatalogId = useSelector<GlobalApplicationState, number | undefined>(
        selectOrganizationCatalogId
    );
    const storePresence = useSelector<
        GlobalApplicationState,
        Partial<ProductsPresenceAcrossStores> | ProductsPresenceAcrossStores
    >(selectMergeProductsStorePresence);

    // Manage Global State
    const dispatch = useDispatch();

    // Local State
    const [brandProduct, setBrandProduct] = useState<ProductInCatalog | undefined>();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [products, setProducts] = useState<ProductInCatalog[]>([]);

    // UI State
    const [invalidFields, setInvalidFields] = useState<ProductData[]>([]);
    const [requiredFields, setRequiredFields] = useState<ProductData[]>([]);

    // Submission States
    const [mergeProductValidationErrors, setMergeProductValidationErrors] =
        useState<ProductValidationErrorReport>({} as ProductValidationErrorReport);
    const [catalogProductInformation, setCatalogProductInformation] =
        useState<CatalogProductInformation>({});
    const [productHasBeenSubmitted, setProductHasBeenSubmitted] = useState<boolean>(false);
    const [mergedProduct, setMergedProduct] = useState<ProductInCatalog>();

    const handleBrandProductSelection = (brandProductSelected: ProductInCatalog) => {
        setBrandProduct(brandProductSelected);
    };

    // Global variables for this file
    let isMerging = false;

    // Component Actions (Functions);
    const backToCatalog = () => {
        let persistedProducts: ProductInCatalog[];
        if (mergedProduct) {
            persistedProducts = [mergedProduct];
        } else if (mergingToExistingProduct && productToMergeTo) {
            persistedProducts = [...products, productToMergeTo as ProductInCatalog];
        } else {
            persistedProducts = products;
        }

        dispatch(highlightMultipleProducts(persistedProducts));

        closeFeedbackModal();
        history.goBack();
        dispatch(clearMergeState());
    };

    const handleMergeError = (error, requestObject) => {
        console.error('Error merging products.', error, requestObject);
        openFeedbackModal(closeFeedbackModal, 'Error merging products', {
            bodyText: 'There was an error merging the products.',
            type: ModalType.ERROR,
            buttonText: 'OK',
        });
    };

    const handleProductValidationError = (
        field: ProductData,
        errorPresent: boolean,
        errorMessage?: string
    ) => {
        if (errorPresent === false) {
            const newErrorStatus = _.omit(mergeProductValidationErrors, [
                field,
            ]) as ProductValidationErrorReport;
            setMergeProductValidationErrors(newErrorStatus);
        } else {
            setMergeProductValidationErrors({
                ...mergeProductValidationErrors,
                [field]: errorMessage,
            } as ProductValidationErrorReport);
        }
    };

    const getCurrentModule = () => {
        return STEPS[currentStep];
    };

    const goToStep = (step: number) => {
        if (_.inRange(step, STEPS.length + 1)) {
            setCurrentStep(step);
        } else {
            console.error(`${step} is not in allowed range.`);
        }
    };

    const previousStep = () => {
        const step = Math.max(currentStep - 1, 0);
        goToStep(step);
    };

    const nextStep = () => {
        const nextStep = Math.min(currentStep + 1, STEPS.length);
        goToStep(nextStep);
    };

    const getProductsToStillMerge = (productToRemoveId: number) => {
        const productsToStillMerge = _.filter(products, (product) => {
            return product.id !== productToRemoveId;
        });

        const productsToStillMergeIds = _.map(productsToStillMerge, 'id');

        return {
            productsToStillMerge,
            productsToStillMergeIds,
        };
    };

    const removeProductFromMerge = (productToRemoveId: number) => {
        const {productsToStillMerge, productsToStillMergeIds} =
            getProductsToStillMerge(productToRemoveId);

        const {pathname, search} = generateProductMergeRoute(
            organizationId,
            productsToStillMergeIds,
            {
                hideExactMatch,
                brandTreezProductId: brandProduct ? brandProduct.id : undefined,
                productToMergeToId: _.get(productToMergeTo, 'id'),
            }
        );

        setProducts(productsToStillMerge);

        history.replace({
            pathname,
            search,
            state: {
                brandProduct,
                hideExactMatch,
                productToMergeTo,
                productsToMerge: productsToStillMerge,
            },
        });
    };

    const selectExistingProductToMergeTo = (existingProductToMergeTo: ProductInCatalog) => {
        const existingProductToMergeToId = _.get(existingProductToMergeTo, 'id');

        if (existingProductToMergeToId == null) {
            console.error(`Product selected to merged to do did not have an id present`);
            openFeedbackModal(closeFeedbackModal, `Error Merging to Existing Product`, {
                type: ModalType.ERROR,
                bodyText: `The product you selected to merge to lacked the required resources to handle the merge, please contact Treez`,
                buttonText: 'OK',
            });
        } else {
            const {productsToStillMerge, productsToStillMergeIds} = getProductsToStillMerge(
                existingProductToMergeTo.id
            );

            const {pathname, search} = generateProductMergeRoute(
                organizationId,
                productsToStillMergeIds,
                {
                    hideExactMatch,
                    brandTreezProductId: brandProductId,
                    productToMergeToId: existingProductToMergeToId,
                }
            );

            history.replace({
                pathname,
                search,
                state: {
                    brandProduct,
                    storePresence,
                    productsToMerge: productsToStillMerge,
                    productToMergeTo: existingProductToMergeTo,
                },
            });
        }
    };

    // API CALLS
    const getBrandProduct = async () => {
        try {
            if (brandProductId) {
                const brandProducts = await getBrandProductsById([Number(brandProductId)]);

                if (_.isEmpty(brandProducts)) {
                    throw new Error(`No product was found for the id ${brandProductId}`);
                } else if (_.size(brandProducts) > 1) {
                    throw new Error(
                        `${brandProducts} has more than one product for the id ${brandProductId}`
                    );
                } else {
                    setBrandProduct(brandProducts[0]);
                }
            }
        } catch (error) {
            dispatch(informUserErrorRetrievingBrandProduct);
        }
    };

    const getProductsStorePresence = async () => {
        try {
            let orgStores = stores;

            if (_.isEmpty(orgStores)) {
                const orgPromise = searchForOrganization(organizationId, dispatch);
                const organization = await orgPromise;

                dispatch(recieveOrganizationInformation(organization));

                orgStores = organization.stores as StoreAttributes[] | undefined;
            }

            const catalogIds = _.map(orgStores, 'catalogId') as number[];

            if (_.size(productIds) > 0 && _.size(catalogIds) > 0 && _.size(stores) > 0) {
                const catalogProductData = await getCatalogProductData(catalogIds, productIds);
                const catalogProductDataWithStoreName = mapStoreNameToCatalogPresence(
                    catalogProductData,
                    stores!
                );

                dispatch(setMergeProductStorePresence(catalogProductDataWithStoreName));
            }
        } catch (error) {
            dispatch(informUserErrorRetrievingStoreInfo);
        }
    };

    const getProductsToMerge = async () => {
        try {
            if (orgCatalogId) {
                const productsToMerge = await getProductsById(productIds, orgCatalogId);
                setProducts(productsToMerge);
            }
        } catch (error) {
            dispatch(informUserErrorRetrievingProductsToMerge);
        }
    };

    const mergeProducts = async () => {
        const catalogIds: number[] = _.map(_.keys(catalogProductInformation), Number);

        // merge to brand product
        if (brandProductId) {
            return mergeProductsIntoExistingProduct(
                brandProductId,
                orgCatalogId!,
                productIds,
                catalogIds,
                catalogProductInformation
            );
        }

        // merge to an existing product
        if (productToMergeTo.id) {
            const existingProductId = productToMergeTo.id! as number;

            const response = await patchProduct(existingProductId, productToMergeTo, orgCatalogId!);

            if (response.status === ACCEPTED) {
                return mergeProductsIntoExistingProduct(
                    existingProductId,
                    orgCatalogId!,
                    productIds,
                    catalogIds,
                    catalogProductInformation
                );
            }
            throw new Error('Failed to save product.');
        }

        const finalProduct = productToMergeTo;

        if (
            productToMergeTo.type &&
            EACH_PRODUCT_TYPES.includes(productToMergeTo.type) &&
            Boolean(productToMergeTo.uom) === false
        ) {
            finalProduct[ProductField.UOM] = UOM.EACH;
        }

        const allNotCannabis = products.every((product) => product.cannabis === false);
        if (allNotCannabis) {
            finalProduct.cannabis = false;
        }

        // merge and create a new product
        return mergeProductsIntoNewProduct(
            orgCatalogId!,
            productIds,
            finalProduct,
            catalogIds,
            catalogProductInformation
        );
    };

    const handleMerge = () => {
        if (isMerging) {
            return;
        }
        dispatch(confirmMerge(onMerge));
    };

    const onMerge = async () => {
        if (isMerging) {
            return;
        }

        try {
            isMerging = true;
            const response = await mergeProducts();
            if (response.status === CREATED) {
                setMergedProduct(response.data);
                goToStep(FINAL_STEP);
                closeFeedbackModal();
            } else {
                const catalogIds: number[] = _.map(_.keys(catalogProductInformation), Number);

                handleMergeError(response, {
                    brandProductId,
                    orgCatalogId,
                    productIds,
                    productToMergeTo,
                    catalogIds,
                    catalogProductInformation,
                });
            }
        } catch (error) {
            const catalogIds: number[] = _.map(_.keys(catalogProductInformation), Number);

            handleMergeError(error, {
                brandProductId,
                orgCatalogId,
                productIds,
                productToMergeTo,
                catalogIds,
                catalogProductInformation,
            });
        } finally {
            isMerging = false;
        }
    };

    // Life Cycle Methods
    useEffect(() => {
        const brandProductInNavData = _.get(location, 'state.brandProduct', {}) as ProductInCatalog;
        const productsInNavData = _.get(
            location,
            'state.productsToMerge',
            []
        ) as ProductInCatalog[];
        const storePresenceInNavData = _.get(location, 'state.storePresence', {}) as Record<
            number,
            ProductStorePresenceAttributes[]
        >;
        const productToMergeToInNavData = _.get(location, 'state.productToMergeTo') as
            | ProductInCatalog
            | undefined;

        const productsInNavDataMatchUrl = _.isEqual(
            _.map(productsInNavData, 'id') as number[],
            productIds
        );

        if (productsInNavDataMatchUrl) {
            setProducts(productsInNavData);
        } else {
            getProductsToMerge();
        }

        if (brandProductInNavData.id === Number(brandProductId)) {
            setBrandProduct(brandProductInNavData);
        } else if (brandProductId) {
            getBrandProduct();
        }

        if (productToMergeToInNavData) {
            dispatch(setMergeToProduct(productToMergeToInNavData));
        } else if (productToMergeToParam && orgCatalogId) {
            getProductsById([productToMergeToParam], orgCatalogId)
                .then((productToMergeToData) => {
                    dispatch(setMergeToProduct(productToMergeToData[0]));
                })
                .catch((error) => {
                    console.error(error);
                });
        }

        if (!_.isEmpty(storePresenceInNavData) && !_.isEmpty(stores) && productIds) {
            const storePresenceWithNames = mapStoreNameToCatalogPresence(
                storePresenceInNavData,
                stores!
            );

            dispatch(setMergeProductStorePresence(storePresenceWithNames));
        } else {
            getProductsStorePresence();
        }
    }, [brandProductId, hideExactMatchParam, productsParam, productToMergeToParam, stores]);

    useEffect(() => {
        const {type: productToMergeToType, subtype: productToMergeToSubtype} = productToMergeTo;

        if (productToMergeToType) {
            const {invalid: invalidFieldsPerType, required: requiredFieldsPerType} =
                requiredAndInvalidProductFieldsByType(
                    productToMergeToType,
                    productToMergeToSubtype
                );

            setInvalidFields(invalidFieldsPerType);
            setRequiredFields(requiredFieldsPerType);
        } else {
            setInvalidFields([]);
            setRequiredFields([NAME, TYPE, ProductField.UOM]);
        }
    }, [productToMergeToType]);

    // Render Properties and Functions

    const renderMainContent = () => {
        const mergeStep: MergeStep = getCurrentModule();

        switch (mergeStep) {
            case MergeStep.MERGE_PRODUCT_DETAILS:
                return (
                    <MergeProductDetails
                        brandProduct={brandProduct}
                        invalidFields={invalidFields}
                        handleProductValidationError={handleProductValidationError}
                        hasBeenSubmitted={productHasBeenSubmitted}
                        hideExactMatch={hideExactMatch}
                        products={products}
                        productValidationErrors={mergeProductValidationErrors}
                        requiredFields={requiredFields}
                        removeProduct={removeProductFromMerge}
                        selectExistingProductToMergeTo={selectExistingProductToMergeTo}
                        setBrandProduct={handleBrandProductSelection}
                    />
                );
            case MergeStep.SELECT_PRICE:
                return (
                    <CatalogAssignment
                        catalogProductInformation={catalogProductInformation}
                        products={products}
                        setCatalogProductInformation={setCatalogProductInformation}
                    />
                );
            default:
                return (
                    <Section>
                        <div className="horizontal-center">
                            <Title size={TitleSize.MEDIUM} text="MERGE COMPLETE" />
                        </div>
                        <div className="horizontal-center">
                            Your products have successfully been merged!
                        </div>
                        <div className="merge-redirect-container">
                            <Button
                                buttonClass={ButtonClass.BASIC}
                                onClick={backToCatalog}
                                text="Back to Catalog"
                            />
                        </div>
                    </Section>
                );
        }
    };

    return (
        <UIWrapper>
            <ProductMergeNav
                backToCatalog={backToCatalog}
                catalogProductInformation={catalogProductInformation}
                currentStep={currentStep}
                handleMerge={handleMerge}
                nextStep={nextStep}
                previousStep={previousStep}
                requiredFields={requiredFields}
                setProductHasBeenSubmitted={setProductHasBeenSubmitted}
                stores={stores || []}
            />
            <div className="merge-page-content-container">{renderMainContent()}</div>
        </UIWrapper>
    );
};

export default ProductMerge;
